import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Info, X } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { LightboxMedia, usePrefetch } from "./LightboxMedia";
import { EventButton } from "./EventButton";
import { ThumbButton } from "./ThumbButton";
import { HeartButton } from "./Hearts";

export const Lightbox = ({ items, index, onIndex, onClose, actions = [], onNearEnd, panel, total, onItemChange }) => {
  const it = items[index];
  const touch = useRef(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const go = useCallback((d) => {
    const n = index + d;
    if (n >= 0 && n < items.length) onIndex(n);
  }, [index, items.length, onIndex]);

  useEffect(() => {
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", key);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", key); document.body.style.overflow = ""; };
  }, [go, onClose]);

  usePrefetch(items, index);

  useEffect(() => {
    if (index >= items.length - 3) onNearEnd?.();
  }, [index, items.length, onNearEnd]);

  if (!it) return null;
  return createPortal(
    <div className={`lightbox ${panel ? "has-panel" : ""}`} data-testid="lightbox-container" onClick={(e) => e.target === e.currentTarget && onClose()}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
        if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
      }}>
      <div className="lightbox-actions">
        {panel && (
          <button className="lb-btn lb-info" onClick={() => setInfoOpen(!infoOpen)} aria-label="Details" data-testid="lightbox-info-button"><Info size={18} /></button>
        )}
        {actions.filter((a) => !a.show || a.show(it)).map((a) => (
          a.href ? (
            <a key={a.key} className="lb-btn" href={mediaUrl(a.href(it))} title={a.label} aria-label={a.label} data-testid={`lightbox-${a.key}-button`}>{a.icon}</a>
          ) : (
            <button key={a.key} className="lb-btn" onClick={() => a.onClick(it)} title={typeof a.label === "function" ? a.label(it) : a.label} aria-label={typeof a.label === "function" ? a.label(it) : a.label} data-testid={`lightbox-${a.key}-button`}>{a.icon}</button>
          )
        ))}
        <HeartButton it={it} onChanged={onItemChange} />
        <ThumbButton it={it} onChanged={onItemChange} />
        <EventButton it={it} onChanged={onItemChange} />
        <button className="lb-btn" onClick={onClose} aria-label="Close" data-testid="lightbox-close-button"><X size={20} /></button>
      </div>
      {total != null && <div className="lb-counter" data-testid="lightbox-counter">{index + 1} / {Math.max(total, items.length)}</div>}
      <button className="lb-nav left" onClick={() => go(-1)} disabled={index <= 0} aria-label="Previous" data-testid="lightbox-prev-button"><ChevronLeft size={28} /></button>
      <button className="lb-nav right" onClick={() => go(1)} disabled={index >= items.length - 1} aria-label="Next" data-testid="lightbox-next-button"><ChevronRight size={28} /></button>
      <div className="lightbox-stage" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <LightboxMedia it={it} />
      </div>
      {panel && <aside className="lightbox-panel">{panel(it)}</aside>}
      {panel && infoOpen && <aside className="lightbox-panel-mobile" data-testid="lightbox-info-sheet">{panel(it)}</aside>}
    </div>,
    document.body,
  );
};
